"use client";

import Image from "next/image";
import { type JSX } from "react";
import { RichText, useSitecore } from "@sitecore-content-sdk/nextjs";

import { useNewsContext } from "contexts/NewsContext";

import Button from "../../../atoms/Button";
import RichTextWithConsent from "../../../atoms/RichTextWithConsent";
import VideoIframe from "../../../atoms/VideoIframe";
import {
  getImageURLForCHPages,
  getLinkTargetValue,
} from "../../../utils/contentHubComponentsUtils";
import { useLocale } from "next-intl";
import { capitalizeLanguageRegion } from "lib/utils";

/**
 * Convert "MM/DD/YYYY" to "September 5, 2023" (English, month spelled out).
 * Returns null if input is invalid.
 *
 * - Stable across time zones by formatting at UTC midnight.
 * - Locale can be changed (defaults to "en-US").
 */
export function toLongUSDate(
  mmddyyyy: string,
  options?: { locale?: string },
): string | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(mmddyyyy.trim());
  if (!m) return null;

  const month = Number(m[1]);
  const day = Number(m[2]);
  const year = Number(m[3]);

  // Validate by constructing a Date in local time, then verifying components
  const test = new Date(year, month - 1, day);
  if (
    test.getFullYear() !== year ||
    test.getMonth() !== month - 1 ||
    test.getDate() !== day
  ) {
    return null; // invalid calendar date like 02/30/2025
  }

  // Build a date at midnight UTC to avoid local TZ offsets when formatting
  const d = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));

  return new Intl.DateTimeFormat(options?.locale ?? "en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}

const getLegacyContent = (body: string) => {
  const pullQuoteH2Style =
    "[&_blockquote_>_h2]:text-text-gray! max-lg:[&_blockquote_>_h2]:text-[20px]! max-lg:[&_blockquote_>_h2]:leading-[28px]! lg:[&_blockquote_>_h2]:text-[24px]! lg:[&_blockquote_>_h2]:tracking-[0.8px]! lg:[&_blockquote_>_h2]:leading-[36px]!";
  const pullQuoteH3Style =
    "[&_blockquote_>_h3]:text-text-gray! max-lg:[&_blockquote_>_h3]:text-[20px]! max-lg:[&_blockquote_>_h3]:leading-[28px]! lg:[&_blockquote_>_h3]:leading-[32.16px]!";

  if (body?.includes("ecolab-youtube-video")) {
    return (
      <RichTextWithConsent
        htmlContent={body}
        className={`ch-legacy-content ${pullQuoteH2Style} ${pullQuoteH3Style}`}
      />
    );
  }
  return (
    <div
      className={`text-base font-normal text-charcoal-black leading-6 tracking-[0.4px] lg:tracking-[0.8px] [&_ul]:list-[revert]! space-y-1 wrap-anywhere mb-5 richtext-content ch-legacy-content ${pullQuoteH2Style} ${pullQuoteH3Style}`}
      dangerouslySetInnerHTML={{ __html: body }}
    ></div>
  );
};

export const Default = (): JSX.Element => {
  console.log("---->[NewsDetails] Component");

  const { page } = useSitecore();
  const editing = page.mode.isEditing;

  const { news } = useNewsContext();

  const locale = useLocale();

  console.log("---->[NewsDetails] news", news);

  const rssTableStyles = `
        .q4default table td{
          border: none;
          padding: 0;
        }
        .q4default .bwalignc {
          text-align: center;
          list-style-position: inside;
          font-style: italic;
        }
        .q4default .bwalignl {
          text-align: left;
        }
        .q4default .bwalignr {
          text-align: right;
          list-style-position: inside;
        }
        .q4default .bwblockalignl {
          margin-left: 0;
          margin-right: auto;
        }
        .q4default .bwcellpmargin {
          margin-bottom: 0;
          margin-top: 0;
        }
        .q4default .bwlistdisc {
          list-style-type: disc;
        }
        .q4default .bwpadb3 {
          padding-bottom: 4px;
        }
        .q4default .bwpadl0 {
          padding-left: 0;
        }
        .q4default .bwpadl2 {
          padding-left: 10px;
        }
        .q4default .bwpadl4 {
          padding-left: 20px;
        }
        .q4default .bwpadr0 {
          padding-right: 0;
        }
        .q4default .bwsinglebottom {
          border-bottom: 1pt solid rgb(0, 0, 0)!important;
        }
        .q4default .bwtablemarginb {
          margin-bottom: 10px;
        }
        .q4default .bwuline {
          text-decoration: underline;
        }
        .q4default .bwvertalignb {
          vertical-align: bottom;
        }
        .q4default .bwvertalignt {
          vertical-align: top;
        }
        .q4default .bwwidth1 {
          width: 1%;
        }
        .q4default .bwwidth100 {
          width: 100%;
        }
        .q4default .bwwidth23 {
          width: 23%;
        }
        .q4default .bwwidth34 {
          width: 34%;
        }
        .q4default .bwwidth52 {
          width: 52%;
        }
        .q4default .bwwidth55 {
          width: 55%;
        }
        .q4default .bwwidth56 {
          width: 56%;
        }
        .q4default .bwwidth64 {
          width: 64%;
        }
        .q4default .bwwidth76 {
          width: 76%;
        }
        .q4default .bwwidth8 {
          width: 8%;
        }
        .q4default .bwwidth9 {
          width: 9%;
        }
      `;

  if (editing) {
    return (
      <div
        className="news-details ch-details mx-auto bg-white w-full lg:w-(--article-container-width) px-(--spacing-sixteen)"
        data-role="news-details-component"
      >
        <h1 className="text-[34px] leading-10.5 tracking-[0.4px] font-medium text-ecolab-blue lg:text-[42px] lg:leading-12 lg:tracking-[0.8px] mt-5 mb-2">
          News Component
        </h1>
      </div>
    );
  }

  const newsReleaseDate = news?.NewsReleaseDate ? news.NewsReleaseDate : null;

  const newsPrimaryImageUrl = getImageURLForCHPages(
    news?.NewsPrimaryImage?.results?.[0]?.assetToPublicLink?.results,
  );
  const newsAdditionalImageUrl = getImageURLForCHPages(
    news?.NewsAdditionalImage?.results?.[0]?.assetToPublicLink?.results,
  );

  return (
    <div className="news-details ch-details bg-white pb-(--global-component-spacing)">
      <style>{rssTableStyles}</style>
      <div className="w-full mx-auto lg:max-w-(--event-detail-container-width) max-lg:px-4">
        {news?.NewsTitle && (
          <h1 className="text-[24px] leading-9 text-ecolab-blue lg:text-3xl font-normal lg:leading-[1.25em] mt-0 lg:mb-0 wrap-anywhere max-lg:self-start max-lg:mb-0 after:content-[''] after:block after:clear-both after:pt-3 after:w-full lg:after:w-100 after:border-b-2 after:border-teal">
            {news?.NewsTitle}
          </h1>
        )}
        {news?.NewSubTitle && (
          <h2 className="text-[14px] font-normal text-neutral-900 leading-5 lg:text-[22px] lg:leading-6 mt-3 mb-5">
            {news?.NewSubTitle}
          </h2>
        )}
        {newsReleaseDate && (
          <span
            className={`inline-block text-base font-normal text-gray leading-[22.86px] mb-5 ${
              news?.NewSubTitle ? "" : "mt-5"
            }`}
          >
            {toLongUSDate(newsReleaseDate, {
              locale: capitalizeLanguageRegion(locale),
            })}
          </span>
        )}
        {news?.NewsMainVideoUrl ? (
          <div className="w-full mb-5">
            <VideoIframe src={news?.NewsMainVideoUrl} />
          </div>
        ) : (
          newsPrimaryImageUrl && (
            <div className="w-full mb-5">
              <Image
                src={newsPrimaryImageUrl}
                alt={"News Image"}
                width={800}
                height={493}
                className="object-cover w-auto h-auto max-w-full rounded-md"
                loading="eager"
                fetchPriority="high"
              />
            </div>
          )
        )}
        {!news?.NewsMainVideoUrl &&
          newsPrimaryImageUrl &&
          news?.NewsMediaCaption && (
            <RichText
              className="text-[14px] italic text-text-gray leading-5 [&_p]:leading-5 [&_p]:text-[14px] [&_ul]:list-[revert]! wrap-anywhere richtext-content mb-5"
              field={{ value: news?.NewsMediaCaption }}
            />
          )}
        {news?.LegacyHtml && getLegacyContent(news.LegacyHtml)}
        {news?.NewContent1 && (
          <RichText
            className="text-base font-normal text-charcoal-black space-y-1 wrap-anywhere richtext-content mb-5 [&_ul]:list-[revert]! tracking-[0.4px] lg:tracking-[0.8px]"
            field={{ value: news?.NewContent1 }}
          />
        )}
        {news?.Newssection2Heading && (
          <h2 className="text-ecolab-blue text-[26px] font-normal leading-7 mb-5">
            {news?.Newssection2Heading}
          </h2>
        )}
        {news?.NewsbodyContent2 && (
          <RichText
            className="text-base font-normal text-charcoal-black space-y-1 wrap-anywhere richtext-content mb-5 [&_ul]:list-[revert]! tracking-[0.4px] lg:tracking-[0.8px]"
            field={{ value: news?.NewsbodyContent2 }}
          />
        )}
        {news?.Newssection3Heading && (
          <h2 className="text-ecolab-blue text-[26px] font-normal leading-7 mb-5">
            {news?.Newssection3Heading}
          </h2>
        )}
        {news?.NewsbodyContent3 && (
          <RichText
            className="text-base font-normal text-charcoal-black space-y-1 wrap-anywhere richtext-content mb-5 [&_ul]:list-[revert]! tracking-[0.4px] lg:tracking-[0.8px]"
            field={{ value: news?.NewsbodyContent3 }}
          />
        )}
      </div>

      {(news?.NewPullQuote ||
        news?.NewsPullQuoteSourceName ||
        news?.NewsPullQuoteSourceTitle) && (
        <div className="bg-light-blue py-(--global-component-spacing) my-8 empty:hidden">
          <div className="mx-auto w-full lg:w-(--event-detail-container-width) max-lg:px-6">
            {news?.NewPullQuote && (
              <h2 className="wrap-anywhere text-[24px] leading-9 antialiased text-ecolab-blue mt-0 font-normal mb-6 lg:text-[36px] lg:leading-11">
                &quot;{news?.NewPullQuote}&quot;
              </h2>
            )}

            {(news?.NewsPullQuoteSourceName ||
              news?.NewsPullQuoteSourceTitle) && (
              <h3 className="wrap-anywhere text-charcoal-black text-[14px] leading-5 antialiased mt-0 font-normal mb-1">
                &#8212;{" "}
                <span className="text-[22px] font-medium leading-6">
                  {news?.NewsPullQuoteSourceName}
                </span>
                ,{" "}
                <span className="text-[22px] font-normal leading-6">
                  {news?.NewsPullQuoteSourceTitle}
                </span>
              </h3>
            )}
          </div>
        </div>
      )}

      <div className="w-full mx-auto lg:max-w-(--event-detail-container-width) max-lg:px-4">
        {news?.NewsAdditionalVideoUrl ? (
          <div className="w-full mb-5">
            <VideoIframe src={news?.NewsAdditionalVideoUrl} />
          </div>
        ) : (
          newsAdditionalImageUrl && (
            <div className="w-full mb-5">
              <Image
                src={newsAdditionalImageUrl}
                alt={"News Image"}
                width={875}
                height={493}
                className="object-cover w-auto max-w-full h-auto rounded-md"
              />
            </div>
          )
        )}
        {news?.Newssection4Heading && (
          <h2 className="text-ecolab-blue text-[26px] font-normal leading-7 mb-5">
            {news?.Newssection4Heading}
          </h2>
        )}
        {news?.NewsbodyContent4 && (
          <RichText
            className="text-base font-normal text-charcoal-black space-y-1 wrap-anywhere richtext-content mb-5 [&_ul]:list-[revert]! tracking-[0.4px] lg:tracking-[0.8px]"
            field={{ value: news?.NewsbodyContent4 }}
          />
        )}

        <div className="flex mb-5 flex-col lg:flex-row lg:justify-center">
          {news?.NewsPrimaryLinkUrl && (
            <Button
              className=""
              field={{
                value: {
                  text: news?.NewsPrimaryLinkText || news.NewsPrimaryLinkUrl,
                  href: news.NewsPrimaryLinkUrl,
                  target: getLinkTargetValue(news?.NewsPrimaryLinkUrl),
                },
              }}
              buttonType="primary"
              data-role="link"
            />
          )}
          {news?.NewsSecondaryLinkCTAUrl && (
            <Button
              className=""
              field={{
                value: {
                  text:
                    news?.NewsSecondaryLinkCTAText ||
                    news?.NewsSecondaryLinkCTAUrl,
                  href: news?.NewsSecondaryLinkCTAUrl,
                  target: getLinkTargetValue(news?.NewsSecondaryLinkCTAUrl),
                },
              }}
              buttonType="secondary"
              data-role="link"
            />
          )}
        </div>
        {news?.NewsBoilerPlate?.boilerPlateTitle &&
          typeof news?.NewsBoilerPlate?.boilerPlateTitle == "string" && (
            <h2 className="text-charcoal-black text-base font-medium mb-2.5 mt-4">
              {news?.NewsBoilerPlate?.boilerPlateTitle}
            </h2>
          )}
        {news?.NewsBoilerPlate?.boilerPlateDescription && (
          <RichText
            className="text-base font-normal text-charcoal-black space-y-1 wrap-anywhere richtext-content mb-4 [&_p]:mb-2.5! [&_ul]:list-[revert]! tracking-[0.4px] lg:tracking-[0.8px]"
            field={{ value: news?.NewsBoilerPlate?.boilerPlateDescription }}
          />
        )}
        {news?.NewsPartnerBoiledPlateHeading && (
          <h2 className="text-charcoal-black text-base font-medium mb-2.5 mt-4">
            {news?.NewsPartnerBoiledPlateHeading}
          </h2>
        )}
        {news?.NewsPartnerBoiledPlateBodyContent && (
          <RichText
            className="text-base font-normal text-charcoal-black space-y-1 wrap-anywhere richtext-content mb-4 [&_p]:mb-2.5! [&_ul]:list-[revert]! tracking-[0.4px] lg:tracking-[0.8px]"
            field={{ value: news?.NewsPartnerBoiledPlateBodyContent }}
          />
        )}
        {news?.Newssection5Heading && (
          <h2 className="text-ecolab-blue text-[26px] font-normal leading-7 mb-2.5 mt-4">
            {news?.Newssection5Heading}
          </h2>
        )}
        {news?.BodyContent5 && (
          <RichText
            className="text-base font-normal text-charcoal-black space-y-1 wrap-anywhere richtext-content mb-4 [&_p]:mb-2.5! [&_ul]:list-[revert]! tracking-[0.4px] lg:tracking-[0.8px]"
            field={{ value: news?.BodyContent5 }}
          />
        )}
        {news?.NewsMediaContactHeading && (
          <h2 className="text-ecolab-blue text-[26px] font-normal leading-7 mb-2.5 mt-4">
            {news.NewsMediaContactHeading}
          </h2>
        )}
        {news?.NewsMediaContactBodyContent && (
          <RichText
            className="font-normal text-gray space-y-1 wrap-anywhere richtext-content mb-5 [&_p]:mb-2.5! [&_ul]:list-[revert]! tracking-[0.4px] lg:tracking-[0.8px]"
            field={{ value: news.NewsMediaContactBodyContent }}
          />
        )}
        {news?.NewsReferenceFootNotesBodyContent && (
          <div className="bg-light-gray text-dark-black text-base p-4.75 rounded-sm border border-platinum-gray shadow-(--shadow-note)">
            <RichText
              className="font-normal text-gray space-y-1 wrap-anywhere [&_p]:mb-2.5! [&_ul]:list-[revert]! richtext-content mb-5 tracking-[0.4] leading-6 lg:tracking-[0.8]"
              field={{ value: news.NewsReferenceFootNotesBodyContent }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
